import { useState, useEffect } from 'react';
import { X, AlertCircle, Loader, Instagram, Save, Plus, Music, HardDrive, Download } from 'lucide-react';
import { useStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { scanInstagramAudio } from '../utils/scanInstagramAudio';

interface EditAdCreativeModalProps {
  creative: {
    id: string,
    content: string,
    platform: 'instagram' | 'tiktok' | 'dropbox' | 'direct_upload',
    status: string,
    videoName?: string | null,
    artists_id?: string,
    release_id?: string | null,
    audio_type?: string | null,
  };
  onClose: () => void;
  isArtistView?: boolean;
}

export function EditAdCreativeModal({ creative, onClose, isArtistView = false }: EditAdCreativeModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoName, setVideoName] = useState(creative.videoName || '');
  const [releases, setReleases] = useState<any[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<string>(creative.release_id || '');
  const [selectedStatus, setSelectedStatus] = useState<string>(creative.status || 'pending');
  const [duplicatingAd, setDuplicatingAd] = useState(false);
  const [scanningAudio, setScanningAudio] = useState(false);
  const [audioType, setAudioType] = useState<string | null>(creative.audio_type ?? null);
  const { fetchAdCreatives } = useStore();

  const handleScanAudio = async () => {
    if (scanningAudio) return;
    setScanningAudio(true);
    toast.loading('Scanning Instagram audio…', { id: 'scan-audio' });
    try {
      const result = await scanInstagramAudio(creative.id, creative.content);
      toast.dismiss('scan-audio');
      if (!result) {
        toast.error('Audio scan failed');
        return;
      }
      if (result.error) {
        toast.error(`Scan error: ${result.error}`);
        return;
      }
      setAudioType(result.audio_type ?? null);
      if (result.licensed && result.downloaded) {
        toast.success('Licensed audio detected — reel promoted as regular IG post', { duration: 6000 });
        await fetchAdCreatives();
        onClose();
      } else if (result.audio_type === 'original_sounds') {
        toast.success('Original sound — eligible for advertising');
        await fetchAdCreatives();
      } else {
        toast.success(`Audio type: ${result.audio_type ?? 'unknown'}`);
      }
    } catch (err) {
      toast.dismiss('scan-audio');
      console.error('Audio scan error:', err);
      toast.error('Audio scan failed');
    } finally {
      setScanningAudio(false);
    }
  };

  // Fetch releases for the artist
  useEffect(() => {
    const fetchReleases = async () => {
      if (!creative.artists_id) return;
      
      try {
        const { data, error } = await supabase
          .from('releases')
          .select('id, name, release_date')
          .eq('artist_id', creative.artists_id as any)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setReleases(data || []);
      } catch (err) {
        console.error('Error fetching releases:', err);
      }
    };
    
    fetchReleases();
  }, [creative.artists_id]);

  
  const handleDuplicateAdWithAuthCode = async () => {
    if (!selectedRelease || creative.platform !== 'tiktok') {
      toast.error('Please select a release first');
      return;
    }

    try {
      setDuplicatingAd(true);
      toast.loading('Duplicating TikTok ad with new auth code...');

      // Call edge function to duplicate ad
      const response = await fetch('https://uydhsjvwrgupgfjevqsz.supabase.co/functions/v1/duplicate-tiktok-ad', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          release_id: selectedRelease,
          new_auth_code: creative.content
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to duplicate ad');
      }

      toast.dismiss();
      toast.success(`Successfully duplicated ad! Ad ID: ${data.ad_id}`);
      await fetchAdCreatives();
      onClose();
    } catch (err) {
      toast.dismiss();
      console.error('Error duplicating ad:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to duplicate ad');
    } finally {
      setDuplicatingAd(false);
    }
  };

  const handleSaveVideoName = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const { error: updateError } = await supabase
        .from('ad_creatives')
        .update({ 
          video_name: videoName || null, 
          release_id: selectedRelease || null,
          status: selectedStatus,
          updated_at: new Date().toISOString() 
        } as any)
        .eq('id' as any, creative.id as any);
      
      if (updateError) throw updateError;
      
      await fetchAdCreatives();
      onClose();
    } catch (err) {
      console.error('Error saving video name:', err);
      setError(err instanceof Error ? err.message : 'Failed to save video name');
    } finally {
      setSaving(false);
    }
  };

  
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: "spring",
        duration: 0.5,
        bounce: 0.3
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold dark:text-white">Edit Ad Creative</h2>
          <motion.button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 -mr-2 touch-manipulation"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="h-5 w-5" />
          </motion.button>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div 
              className="mb-4 flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-6">
          {/* Video Name Input */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <label className="block text-sm font-medium mb-2 dark:text-white">
              Video Name
            </label>
            {isArtistView ? (
              <input
                type="text"
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
                placeholder="Enter video name"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white px-3 py-2"
              />
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={videoName}
                  onChange={(e) => setVideoName(e.target.value)}
                  placeholder="Enter video name"
                  className="flex-1 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white px-3 py-2"
                />
                <motion.button
                  onClick={handleSaveVideoName}
                  disabled={saving}
                  className="btn px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={!saving ? { scale: 1.05 } : {}}
                  whileTap={!saving ? { scale: 0.95 } : {}}
                >
                  {saving ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </motion.button>
              </div>
            )}
          </motion.div>

          {/* Release Selection - only show if there are releases */}
          {releases.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.03 }}
            >
              <label className="block text-sm font-medium mb-2 dark:text-white">
                Release
              </label>
              <select
                value={selectedRelease}
                onChange={(e) => setSelectedRelease(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white px-3 py-2"
              >
                <option value="">No Release Assigned</option>
                {releases.map((release: any) => {
                  const releaseDate = new Date(release.release_date);
                  const formattedDate = `${releaseDate.getDate()}.${releaseDate.getMonth() + 1}.${releaseDate.getFullYear()}`;
                  return (
                    <option key={release.id} value={release.id}>
                      {release.name} ({formattedDate})
                    </option>
                  );
                })}
              </select>
            </motion.div>
          )}

          {/* Status Selection - Admin only */}
          {!isArtistView && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.04 }}
            >
              <label className="block text-sm font-medium mb-2 dark:text-white">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white px-3 py-2"
              >
                <option value="pending">Pending</option>
                <option value="ready">Ready</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </motion.div>
          )}

          {/* Content Display */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            <label className="block text-sm font-medium mb-2 dark:text-white">
              Content
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {creative.platform === 'instagram' ? (
                    <Instagram className="h-5 w-5 text-white" />
                  ) : creative.platform === 'tiktok' ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-5 w-5 text-white"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  ) : (
                    <HardDrive className="h-5 w-5 text-white" />
                  )}
                  <span className="text-sm font-medium dark:text-gray-200">
                    {creative.platform === 'direct_upload'
                      ? 'File'
                      : creative.platform.charAt(0).toUpperCase() + creative.platform.slice(1)}
                  </span>
                </div>
                {creative.platform === 'instagram' && creative.content?.includes('instagram.com') && (
                  <div className="flex items-center gap-2">
                    {audioType && (
                      <span
                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md ${
                          audioType === 'licensed_music'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                            : audioType === 'original_sounds'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                        title={`Audio type: ${audioType}`}
                      >
                        <Music className="h-3 w-3" />
                        {audioType === 'licensed_music'
                          ? 'Licensed — cannot advertise'
                          : audioType === 'original_sounds'
                          ? 'Original sound'
                          : audioType}
                      </span>
                    )}
                    <motion.button
                      onClick={handleScanAudio}
                      disabled={scanningAudio}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      whileHover={!scanningAudio ? { scale: 1.05 } : {}}
                      whileTap={!scanningAudio ? { scale: 0.95 } : {}}
                      title="Scan audio type; if licensed, download video to storage"
                    >
                      {scanningAudio ? (
                        <Loader className="h-3 w-3 animate-spin" />
                      ) : (
                        <Music className="h-3 w-3" />
                      )}
                      <span>
                        {scanningAudio
                          ? 'Scanning…'
                          : audioType
                          ? 'Re-scan'
                          : 'Scan audio'}
                      </span>
                    </motion.button>
                  </div>
                )}
                {creative.platform === 'tiktok' && !isArtistView && selectedRelease && (
                  <motion.button
                    onClick={handleDuplicateAdWithAuthCode}
                    disabled={duplicatingAd}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={!duplicatingAd ? { scale: 1.05 } : {}}
                    whileTap={!duplicatingAd ? { scale: 0.95 } : {}}
                    title="Duplicate existing ad with this auth code"
                  >
                    {duplicatingAd ? (
                      <Loader className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    <span>Duplicate Ad</span>
                  </motion.button>
                )}
              </div>
              <pre className="text-sm bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-600 overflow-x-auto">
                {creative.content}
              </pre>
              {creative.platform === 'direct_upload' && creative.content?.startsWith('http') && (
                <div className="mt-2 flex justify-end">
                  <a
                    href={creative.content}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white rounded-md"
                    title="Download video file"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div 
            className="flex justify-end space-x-2 pt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <motion.button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              disabled={saving}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            {isArtistView && (
              <motion.button
                onClick={handleSaveVideoName}
                disabled={saving}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={!saving ? { scale: 1.05 } : {}}
                whileTap={!saving ? { scale: 0.95 } : {}}
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </motion.button>
            )}
            {!isArtistView && (
              <motion.button
                onClick={handleSaveVideoName}
                disabled={saving}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={!saving ? { scale: 1.05 } : {}}
                whileTap={!saving ? { scale: 0.95 } : {}}
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </motion.button>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}